import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "@/features/axiosHelper";
import dayjs from "dayjs";

// Note: dateFilter now lives in sessionInfo.dateFilter.home (dateFilterSlice)
// Removed legacy localStorage preference logic and dateFilter state from this slice.

// Second Node: selectedWorkflow now lives in sessionInfo.workflowManagerSession (workflowManagerSessionSlice)
// Removed old selectedWorkflow reducer and selector

const homeSlice = createSlice({
  name: "home",
  initialState: {
    selectedLifecycle: 1010,
    hoveredWorkflow: 0,
    openWorkflowTab: 0,
    selectedConnection: "All",
    connectionWorkflows: [],
    statusCount: [],
    mapDate: [],
    summary: {
      workflows: [],
      workflowsLoaded: false,
    },
    status: "idle",
    error: null,
    viewDataPopup: {
      open: false,
      selectedRecord: {},
      data: {
        header: "default",
        rawDataSource: "default",
        structuredDataSource: "default",
        rawDataTarget: "default",
        structuredDataTarget: "default",
        catalogData: "default",
      },
    },
    workflowPopupOpen: false,
    orderLifecyclePopupOpen: false,
    workflowSettingsPopupOpen: false,
    message: {
      filters: {
        sender: [],
        type: [],
        read: false,
        unread: false,
        pinned: false,
      },
    },
  },
  reducers: {
    setSelectedLifecycle: (state, action) => {
      const { selectedLifecycle } = action.payload;
      state.selectedLifecycle = selectedLifecycle;
    },
    setHoveredWorkflow: (state, action) => {
      const { hoveredWorkflow } = action.payload;
      state.hoveredWorkflow = hoveredWorkflow;
    },
    setSelectedConnection: (state, action) => {
      const { selectedConnection } = action.payload;
      state.selectedConnection = selectedConnection;
    },
    setViewDataPopupOpen: (state, action) => {
      state.viewDataPopup.open = action.payload;
      if (!action.payload) {
        state.viewDataPopup.data = {
          header: "default",
          rawDataSource: "default",
          structuredDataSource: "default",
          rawDataTarget: "default",
          structuredDataTarget: "default",
          catalogData: "default",
        };
      }
    },

    setViewDataPopupRecord: (state, action) => {
      const { selectedRecord } = action.payload;
      state.viewDataPopup.selectedRecord = selectedRecord;
    },
    setViewDataPopupData: (state, action) => {
      const { data } = action.payload;
      state.viewDataPopup.data = data;
    },
    setWorkflowSettingsPopupOpen: (state, action) => {
      state.workflowSettingsPopupOpen = action.payload;
    },
    setOpenWorkflowTab: (state, action) => {
      const { tabNum } = action.payload;
      state.openWorkflowTab = tabNum;
    },
    //SHEP-183 Message popup filter related methods
    setMessageFilterSender: (state, action) => {
      const { value } = action.payload;
      state.message.filters.sender = value;

      // heap.io
      heapIOTrackMessageFilter(
        "From",
        action.payload.isInitValue,
        action.payload.value
      );
    },
    setMessageFilterType: (state, action) => {
      const { value } = action.payload;
      state.message.filters.type = value;

      // heap.io
      heapIOTrackMessageFilter(
        "Message Type",
        action.payload.isInitValue,
        action.payload.value
      );
    },
    setMessageFilterRead: (state, action) => {
      const { value } = action.payload;
      state.message.filters.read = value;

      // heap.io
      heapIOTrackMessageFilter(
        "Read",
        action.payload.isInitValue,
        action.payload.value
      );
    },
    setMessageFilterUnread: (state, action) => {
      const { value } = action.payload;
      state.message.filters.unread = value;

      // heap.io
      heapIOTrackMessageFilter(
        "UnRead",
        action.payload.isInitValue,
        action.payload.value
      );
    },
    setMessageFilterPinned: (state, action) => {
      const { value } = action.payload;
      state.message.filters.pinned = value;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getWorkflows.pending, (state, action) => {
        state.status = "loading";
        state.summary.workflowsLoaded = false;
      })
      .addCase(getWorkflows.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
        state.summary.workflowsLoaded = true;
      })
      .addCase(getWorkflows.fulfilled, (state, action) => {
        state.status = "loaded";
        // If thunk returned null (dateFilter not ready) keep existing workflows
        if (action.payload && action.payload.data) {
          state.summary.workflows = action.payload.data;
        }
        state.summary.workflowsLoaded = true;
      })
      .addCase(getConnectionWorkflows.pending, (state, action) => {
        state.status = "loading";
      })
      .addCase(getConnectionWorkflows.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      })
      .addCase(getConnectionWorkflows.fulfilled, (state, action) => {
        state.status = "loaded";
        state.connectionWorkflows = action.payload.data;
      })
      .addCase(fetchViewDataPopupData.fulfilled, (state, action) => {
        state.viewDataPopup.data = action.payload;
      });
  },
});

function heapIOTrackMessageFilter(filterType, isInitValue, filterState) {
  // Do not track if value is being initialized, separate from user input
  if (isInitValue) {
    return;
  }

  const eventProps = {};

  let filterText;

  switch (filterType) {
    case "Message Type":
    case "From":
      const labelField =
        filterType === "Message Type" ? "label" : "DisplayName";

      const selectedItems = filterState
        .filter((item) => item.isChecked)
        .map((item) => item[labelField]);

      if (selectedItems.length === filterState.length) {
        filterText = "All";
      } else if (selectedItems.length === 0) {
        filterText = "None";
      } else {
        filterText = selectedItems.join(",");
      }

      break;
    case "Read":
    case "UnRead":
      filterText = filterState;
      break;
  }

  eventProps[`Filter - ${filterType}`] = filterText;

  window.heap.track("Messages - Update Filter", eventProps);
}

export const fetchViewDataPopupData = createAsyncThunk(
  "home/fetchViewDataPopupData",
  async (
    {
      viewType,
      sourceReferenceId,
      targetReferenceId,
      operationId,
      connectionPairId,
    },
    { dispatch }
  ) => {
    try {
      const response = await axios.get("/operationsmanager/source-data", {
        params: {
          viewType: viewType,
          sourceReferenceId: sourceReferenceId,
          targetReferenceId: targetReferenceId,
          operationId: operationId,
          connectionPairId: connectionPairId,
        },
      });

      const data = {
        rawDataSource: response.data.rawSourceDetails || "Nothing to Display",
        structuredDataSource:
          JSON.parse(response.data.structuredDataSource) ||
          "Nothing to Display",
        rawDataTarget: response.data.rawTargetDetails || "Nothing to Display",
        structuredDataTarget:
          JSON.parse(response.data.structuredDataTarget) ||
          "Nothing to Display",
        catalogData: response.data.catalogData,
      };

      dispatch(setViewDataPopupData({ data }));
      return data;
    } catch (error) {
      alert("Encountered problem displaying data");
      throw error;
    }
  }
);

export const getWorkflows = createAsyncThunk(
  "home/getWorkflows",
  async (_, { getState }) => {
    const state = getState();
    const dateFilter = state.sessionInfo?.dateFilter?.home;
    // Block API call until date filter ready
    if (!dateFilter || !dateFilter.startDate || !dateFilter.endDate) {
      return null; // handled in fulfilled reducer
    }
    let connectionId =
      state.tempInfo.home.selectedConnection === "All"
        ? null
        : state.tempInfo.home.selectedConnection.ConnectionId;

    const response = await axios.get(
      "/operationsmanager/workflow-status-details/",
      {
        params: {
          lifecycle: state.tempInfo.home.selectedLifecycle,
          startDate: dateFilter.startDate,
          endDate: dateFilter.endDate,
          connectionId: connectionId,
        },
      }
    );
    return response;
  }
);
export const getConnectionWorkflows = createAsyncThunk(
  "home/getConnectionWorkflows",
  async (_, { getState }) => {
    const state = getState();

    return await axios.get("/operationsmanager/workflows", {
      params: {
        lifecycle: state.tempInfo.home.selectedLifecycle,
        connectionId: state.tempInfo.home.selectedConnection.connectionId,
      },
    });
  }
);

export const selectLifecycle = (state) => state.tempInfo.home.selectedLifecycle;
// Updated to reference centralized dateFilter slice
export const selectDateFilter = (state) => state.sessionInfo.dateFilter.home;
export const selectHoveredWorkflow = (state) =>
  state.tempInfo.home.hoveredWorkflow;
export const selectConnection = (state) =>
  state.tempInfo.home.selectedConnection;
export const selectAllWorkflows = (state) =>
  state.tempInfo.home.summary.workflows;
export const selectWorkflowsLoaded = (state) =>
  state.tempInfo.home.summary.workflowsLoaded;
export const selectViewDataPopupOpen = (state) =>
  state.tempInfo.home.viewDataPopup.open;
export const selectViewDataPopupRecord = (state) =>
  state.tempInfo.home.viewDataPopup.selectedRecord;
export const selectViewDataPopupData = (state) =>
  state.tempInfo.home.viewDataPopup.data;
export const selectWorkflowSettingsPopupOpen = (state) =>
  state.tempInfo.home.workflowSettingsPopupOpen;
export const selectConnectionWorkflows = (state) =>
  state.tempInfo.home.connectionWorkflows;
export const selectOpenWorkflowTab = (state) =>
  state.tempInfo.home.openWorkflowTab;

//SHEP-183: Message popup filter related methods
export const selectMessageFilterSender = (state) =>
  state.tempInfo.home.message.filters.sender;
export const selectMessageFilterType = (state) =>
  state.tempInfo.home.message.filters.type;
export const selectMessageFilterRead = (state) =>
  state.tempInfo.home.message.filters.read;
export const selectMessageFilterUnread = (state) =>
  state.tempInfo.home.message.filters.unread;
export const selectMessageFilterPinned = (state) =>
  state.tempInfo.home.message.filters.pinned;

export const {
  setSelectedLifecycle,
  setHoveredWorkflow,
  setSelectedConnection,
  setViewDataPopupOpen,
  setViewDataPopupRecord,
  setViewDataPopupData,
  setWorkflowSettingsPopupOpen,
  setOpenWorkflowTab,

  //SHEP-183 Message popup filter related methods
  setMessageFilterSender,
  setMessageFilterType,
  setMessageFilterRead,
  setMessageFilterUnread,
  setMessageFilterPinned,
} = homeSlice.actions;

export default homeSlice.reducer;
